import React, { useEffect, useState, useRef } from 'react';
import './style.css';

// Progress stages with their target percentages
const STAGES = {
  STARTING: { percent: 5, label: 'Starting...' },
  FETCHING_IMAGE: { percent: 15, label: 'Fetching image...' },
  IMAGE_DOWNLOADED: { percent: 30, label: 'Image fetched' },
  COMPRESSING: { percent: 45, label: 'Optimizing...' },
  COMPRESSED: { percent: 55, label: 'Image ready' },
  SENDING_API: { percent: 65, label: 'Searching...' },
  API_PROCESSING: { percent: 80, label: 'Found products...' },
  ALMOST_DONE: { percent: 92, label: 'Almost done...' },
  COMPLETE: { percent: 100, label: 'Similar products are..!' },
  ERROR: { percent: 0, label: 'Failed' },
};

// Singleton state management
let progressState = {
  isVisible: false,
  currentPercent: 0,
  targetPercent: 0,
  stage: 'STARTING',
  listeners: new Set(),
};

const notifyListeners = () => {
  progressState.listeners.forEach(listener => listener({ ...progressState }));
};

// Public API to control the progress loader
export const SimilarSearchProgressAPI = {
  show: () => {
    progressState = {
      ...progressState,
      isVisible: true,
      currentPercent: 0,
      targetPercent: STAGES.STARTING.percent,
      stage: 'STARTING',
    };
    notifyListeners();
  },
  
  hide: () => {
    progressState = {
      ...progressState,
      isVisible: false,
      currentPercent: 0,
      targetPercent: 0,
      stage: 'STARTING',
    };
    notifyListeners();
  },
  
  setStage: (stageName) => {
    const stage = STAGES[stageName];
    if (stage) {
      progressState = {
        ...progressState,
        targetPercent: stage.percent,
        stage: stageName,
      };
      notifyListeners();
    }
  },
  
  setPercent: (percent) => {
    progressState = {
      ...progressState,
      targetPercent: Math.min(100, Math.max(0, percent)),
    };
    notifyListeners();
  },
  
  complete: () => {
    progressState = {
      ...progressState,
      targetPercent: 100,
      stage: 'COMPLETE',
    };
    notifyListeners();
    setTimeout(() => {
      SimilarSearchProgressAPI.hide();
    }, 600);
  },
  
  error: () => {
    progressState = {
      ...progressState,
      stage: 'ERROR',
    };
    notifyListeners();
    setTimeout(() => {
      SimilarSearchProgressAPI.hide();
    }, 1200);
  },
};

// Compact progress component
export default function SimilarSearchProgress() {
  const [state, setState] = useState({ ...progressState });
  const [displayPercent, setDisplayPercent] = useState(0);
  const animationRef = useRef(null);
  const autoProgressRef = useRef(null);
  
  // Subscribe to state changes
  useEffect(() => {
    const listener = (newState) => setState({ ...newState });
    progressState.listeners.add(listener);
    return () => {
      progressState.listeners.delete(listener);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (autoProgressRef.current) clearInterval(autoProgressRef.current);
    };
  }, []);
  
  // Smooth animation towards target percent
  useEffect(() => {
    if (!state.isVisible) {
      setDisplayPercent(0);
      return;
    }
    
    const animate = () => {
      setDisplayPercent(current => {
        const diff = state.targetPercent - current;
        if (Math.abs(diff) < 0.5) return state.targetPercent;
        return current + diff * 0.15;
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [state.isVisible, state.targetPercent]);
  
  // Auto-progress during API_PROCESSING stage
  useEffect(() => {
    if (state.stage === 'API_PROCESSING' || state.stage === 'SENDING_API') {
      autoProgressRef.current = setInterval(() => {
        progressState.targetPercent = Math.min(
          progressState.targetPercent + 0.5,
          STAGES.ALMOST_DONE.percent - 1
        );
        notifyListeners();
      }, 300);
    } else {
      if (autoProgressRef.current) {
        clearInterval(autoProgressRef.current);
        autoProgressRef.current = null;
      }
    }
    
    return () => {
      if (autoProgressRef.current) {
        clearInterval(autoProgressRef.current);
      }
    };
  }, [state.stage]);
  
  if (!state.isVisible) return null;
  
  const stageInfo = STAGES[state.stage] || STAGES.STARTING;
  const roundedPercent = Math.round(displayPercent);
  const isError = state.stage === 'ERROR';
  const isComplete = state.stage === 'COMPLETE';
  
  // Calculate circle properties
  const size = 32;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - displayPercent / 100);

  return (
    <div className={`similar-progress-compact ${isError ? 'error' : ''} ${isComplete ? 'complete' : ''}`}>
      {/* Small circular progress */}
      <div className="progress-circle-small">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            className="progress-bg-small"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
          />
          <circle
            className={`progress-arc-small ${isError ? 'error' : ''} ${isComplete ? 'complete' : ''}`}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
            }}
          />
        </svg>
        {/* Percentage inside circle */}
        <span className={`progress-percent-small ${isComplete ? 'complete' : ''} ${isError ? 'error' : ''}`}>
          {isComplete ? '✓' : isError ? '✕' : roundedPercent}
        </span>
      </div>
      
      {/* Text */}
      <div className="progress-text-small">
        <span className="progress-label-small">{stageInfo.label}</span>
        {!isComplete && !isError && (
          <span className="progress-sublabel">{roundedPercent}% complete</span>
        )}
      </div>
    </div>
  );
}
